import { gql, useQuery } from '@apollo/client'
import IMAGE_FRAGMENT, { IMAGE_COLLECTION_FRAGMENT } from './IMAGE_FRAGMENT'
import { getEntityFilterKey } from '../util'

const PROJECT_LOCATION_ENTITY_FRAGMENT = gql`
    fragment ProjectLocationEntity on ProjectLocationEntity {
        id
        attributes {
            Name
            Name_Fr
        }
    }
`
const PROJECT_LOCATION_ENTITY_RESPONSE_FRAGMENT = gql`
    fragment ProjectLocationEntityResponse on ProjectLocationEntityResponse {
        data {
            ...ProjectLocationEntity
        }
    }
    ${PROJECT_LOCATION_ENTITY_FRAGMENT}
`
const PROJECT_MECHANISM_ENTITY_FRAGMENT = gql`
    fragment ProjectMechanismEntity on ProjectMechanismEntity {
        id
        attributes {
            Name
            Name_Fr
        }
    }
`
const PROJECT_MECHANISM_ENTITY_RESPONSE_FRAGMENT = gql`
    fragment ProjectMechanismEntityResponse on ProjectMechanismEntityResponse {
        data {
            ...ProjectMechanismEntity
        }
    }
    ${PROJECT_MECHANISM_ENTITY_FRAGMENT}
`
const PROJECT_TECHNOLOGY_ENTITY_FRAGMENT = gql`
    fragment ProjectTechnologyEntity on ProjectTechnologyEntity {
        id
        attributes {
            Name
            Name_Fr
        }
    }
`
const PROJECT_TECHNOLOGY_ENTITY_RESPONSE_FRAGMENT = gql`
    fragment ProjectTechnologyEntityResponse on ProjectTechnologyEntityResponse {
        data {
            ...ProjectTechnologyEntity
        }
    }
    ${PROJECT_TECHNOLOGY_ENTITY_FRAGMENT}
`
const PROJECT_FRAGMENT = gql`
    ${IMAGE_COLLECTION_FRAGMENT}
    ${IMAGE_FRAGMENT}
    fragment ProjectFields on Project {
        Image {
            ...ImageCollectionFields
        }
        Hero_Image {
            ...ImageFields
        }
        Verification_Logo {
            ...ImageFields
        }
        Section_Image {
            ...ImageFields
        }
        SEO_URL
        URL
        Card_Image {
            ...ImageFields
        }
        Project_Location {
            ...ProjectLocationEntityResponse
        }
        Project_Mechanism {
            ...ProjectMechanismEntityResponse
        }
        Project_Technology {
            ...ProjectTechnologyEntityResponse
        }
        handle
    }
    ${PROJECT_LOCATION_ENTITY_RESPONSE_FRAGMENT}
    ${PROJECT_MECHANISM_ENTITY_RESPONSE_FRAGMENT}
    ${PROJECT_TECHNOLOGY_ENTITY_RESPONSE_FRAGMENT}
`
export const PROJECT_SECTION_FRAGMENT = gql`
    ${IMAGE_FRAGMENT}
    fragment ProjectSectionFields on ProjectEntity {
        id
        attributes {
            Verification_Logo {
                ...ImageFields
            }
            SEO_URL
            Section_Image {
                ...ImageFields
            }
            handle
        }
    }
`

export const PROJECTS_COLLECTION_FRAGMENT = gql`
    ${PROJECT_SECTION_FRAGMENT}
    fragment ProjectCollectionFields on ProjectRelationResponseCollection {
        data {
            ...ProjectSectionFields
        }
    }
`

export const PROJECTS_COLLECTION_FRAGMENT_CUSTOM_API = gql`
    ${PROJECT_SECTION_FRAGMENT}
    fragment ProjectCollectionFields on ProjectEntityResponseCollection {
        data {
            ...ProjectSectionFields
        }
    }
`

export default PROJECT_FRAGMENT

const PROJECT_LOCATIONS_QUERY = gql`
    query ProjectLocations {
        projectLocations {
            data {
                ...ProjectLocationEntity
            }
        }
    }
    ${PROJECT_LOCATION_ENTITY_FRAGMENT}
`
export type ProjectLocationEntity = {
    id: string
    attributes: {
        Name: string
        Name_Fr: string
    }
}
export function useProjectLocations(skip = false) {
    const filterKey = getEntityFilterKey()
    const { data, loading } = useQuery(PROJECT_LOCATIONS_QUERY, {
        variables: {
            sort: [filterKey]
        },
        skip
    })
    return {
        loading,
        projectLocations: (data?.projectLocations?.data || []) as ProjectLocationEntity[]
    }
}

const PROJECT_MECHANISMS_QUERY = gql`
    query ProjectMechanisms {
        projectMechanisms {
            data {
                ...ProjectMechanismEntity
            }
        }
    }
    ${PROJECT_MECHANISM_ENTITY_FRAGMENT}
`
export type ProjectMechanismEntity = {
    id: string
    attributes: {
        Name: string
        Name_Fr: string
    }
}
export function useProjectMechanisms(skip = false) {
    const filterKey = getEntityFilterKey()
    const { data, loading } = useQuery(PROJECT_MECHANISMS_QUERY, {
        variables: {
            sort: [filterKey]
        },
        skip
    })
    return {
        loading,
        projectMechanisms: (data?.projectMechanisms?.data || []) as ProjectMechanismEntity[]
    }
}

const PROJECT_TECHNOLOGIES_QUERY = gql`
    query ProjectTechnologies {
        projectTechnologies {
            data {
                ...ProjectTechnologyEntity
            }
        }
    }
    ${PROJECT_TECHNOLOGY_ENTITY_FRAGMENT}
`
export type ProjectTechnologyEntity = {
    id: string
    attributes: {
        Name: string
        Name_Fr: string
    }
}
export function useProjectTechnologies(skip = false) {
    const filterKey = getEntityFilterKey()
    const { data, loading } = useQuery(PROJECT_TECHNOLOGIES_QUERY, {
        variables: {
            sort: [filterKey]
        },
        skip
    })
    return {
        loading,
        projectTechnologies: (data?.projectTechnologies?.data || []) as ProjectTechnologyEntity[]
    }
}
